const DEFAULT_DURATION_MS = 60 * 60 * 1000;

function parseMeetingDate(value?: string | Date | null): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return null;
  return date;
}

function formatGoogleCalendarDate(date: Date): string {
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function formatIcsDate(date: Date): string {
  return formatGoogleCalendarDate(date);
}

function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

function resolveMeetingUrl({ url }: { url?: string }): string {
  if (!url) return window.location.origin;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${window.location.origin}${url.startsWith("/") ? url : `/${url}`}`;
}

function resolveEventEnd({
  start,
  end,
}: {
  start: Date;
  end?: string | Date | null;
}): Date {
  const endDate = parseMeetingDate(end);
  if (endDate && endDate > start) return endDate;
  return new Date(start.getTime() + DEFAULT_DURATION_MS);
}

export function buildMeetingCalendarEvent({
  title,
  start,
  end,
  url,
}: {
  title: string;
  start: string | Date;
  end?: string | Date | null;
  url?: string;
}) {
  const startDate = parseMeetingDate(start);
  if (!startDate) return null;

  const endDate = resolveEventEnd({ start: startDate, end });
  const eventUrl = resolveMeetingUrl({ url });
  const description = `Join at ${eventUrl}`;
  const uid = `meeting-${startDate.getTime()}@${window.location.hostname}`;

  const googleCalendarUrl = new URL(
    "https://calendar.google.com/calendar/render",
  );
  googleCalendarUrl.searchParams.set("action", "TEMPLATE");
  googleCalendarUrl.searchParams.set("text", title);
  googleCalendarUrl.searchParams.set(
    "dates",
    `${formatGoogleCalendarDate(startDate)}/${formatGoogleCalendarDate(endDate)}`,
  );
  googleCalendarUrl.searchParams.set("details", description);
  googleCalendarUrl.searchParams.set("location", eventUrl);

  const outlookUrl = new URL(
    "https://outlook.live.com/calendar/0/deeplink/compose",
  );
  outlookUrl.searchParams.set("path", "/calendar/action/compose");
  outlookUrl.searchParams.set("rru", "addevent");
  outlookUrl.searchParams.set("subject", title);
  outlookUrl.searchParams.set("body", description);
  outlookUrl.searchParams.set("location", eventUrl);
  outlookUrl.searchParams.set("startdt", startDate.toISOString());
  outlookUrl.searchParams.set("enddt", endDate.toISOString());

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Fanrank//Meeting//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `DTSTART:${formatIcsDate(startDate)}`,
    `DTEND:${formatIcsDate(endDate)}`,
    `SUMMARY:${escapeIcsText(title)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    `URL:${eventUrl}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  return {
    googleCalendarUrl: googleCalendarUrl.toString(),
    outlookUrl: outlookUrl.toString(),
    icsContent,
    icsFilename: `${title.replace(/[^\w\s-]/g, "").trim() || "meeting"}.ics`,
  };
}

export function downloadMeetingIcs({
  title,
  start,
  end,
  url,
}: {
  title: string;
  start: string | Date;
  end?: string | Date | null;
  url?: string;
}) {
  const event = buildMeetingCalendarEvent({ title, start, end, url });
  if (!event) return;

  const blob = new Blob([event.icsContent], {
    type: "text/calendar;charset=utf-8",
  });
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = event.icsFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}
